// Admin "view as" (2026-09-25): how a teacher or student dashboard call is
// answered while an admin views as someone.
//
// teacherApi() and studentApi() hand every call here when view mode is on.
// Reads go to the admin API's viewAs action, which runs the REAL teacher /
// student API code as that person, read only (claude-admin-api view-as.mjs).
// The server refuses anything not on its read list with 409, which shows here
// as "View only: nothing was saved." A few student actions are answered in the
// browser so an admin can try the student side: homework answers are graded
// locally (localHomework.js) and never sent.
import { ADMIN_API_URL } from '../config/api';
import { getViewAs, subscribeViewAs, viewerOf } from './viewAsStore';
import { resetLocalHomework, seedHomework, submitLocalAnswer } from './localHomework';

export const VIEW_ONLY_MESSAGE = 'View only: nothing was saved.';
export const BLOCKED_EVENT = 'bb:viewas-blocked';

// The banner listens for this and says a change was not saved.
export function notifyBlocked(detail = {}) {
  try { window.dispatchEvent(new CustomEvent(BLOCKED_EVENT, { detail })); } catch { /* no window */ }
}

// The active view of that kind, or null.
export function viewingAs(kind) {
  const va = getViewAs();
  return va && va.kind === kind ? va : null;
}

// A new person (or no one) means a fresh try-it state.
subscribeViewAs(() => resetLocalHomework());

export async function viewAsCall(auth, as, call, params = {}, { signal } = {}) {
  const va = viewingAs(as);
  if (!va) {
    const err = new Error('View mode has ended.');
    err.status = 0;
    throw err;
  }
  const { user, handleAPIResponse } = auth || {};
  const token = user?.accessToken; // the admin's own token: the admin API checks it
  if (!token) {
    const err = new Error('not signed in');
    err.status = 401;
    throw err;
  }
  const body = { action: 'viewAs', as, targetId: va.id, call, params: params || {} };
  // A teacher's view names the class: the admin API checks the caller teaches
  // it and the student is enrolled in it (2026-10-27).
  if (viewerOf(va) === 'Teacher') body.classID = va.classId;
  let res = await fetch(ADMIN_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
    signal,
  });
  // Only an expired admin token is worth a refresh + retry (the admin API
  // never answers 401 for anything else in view mode).
  if (res.status === 401 && handleAPIResponse) res = await handleAPIResponse(res, body);
  let data = null;
  try { data = await res.json(); } catch { /* non-json error body */ }
  if (!res.ok) {
    // 409 without a reason = the call would change data.
    const viewOnly = res.status === 409 && !data?.reason;
    if (viewOnly) notifyBlocked({ as, call });
    const err = new Error(viewOnly ? VIEW_ONLY_MESSAGE : (data?.error || `view ${call} failed (${res.status})`));
    err.status = res.status;
    err.data = data;
    err.viewOnly = viewOnly;
    throw err;
  }
  return data;
}

export const teacherViewAsCall = (auth, action, params, options) => viewAsCall(auth, 'teacher', action, params, options);

// Student actions answered in the browser: try it, never saved.
const LOCAL_STUDENT = {
  submitHomeworkAnswer: (p) => submitLocalAnswer(p),
  updateHomeworkWalk: () => ({ ok: true, viewOnly: true }),
  // an open assessment's Submit (2026-10-26): a write the admin API refuses; view-as answers it locally so the done card shows
  submitAssessment: () => ({ ok: true, done: true, submittedAt: new Date().toISOString(), viewOnly: true }),
  dismissReminder: () => ({ ok: true, viewOnly: true }),
  // The dashboard's placement self-heal: silently skipped (it would save levels).
  placeFromDiagnostic: () => ({ placed: {}, viewOnly: true }),
};

export async function studentViewAsCall(auth, action, params = {}) {
  const local = LOCAL_STUDENT[action];
  if (local) return local(params);
  const data = await viewAsCall(auth, 'student', action, params);
  if (action === 'startHomework') seedHomework(params, data);
  return data;
}
